/**
 * eSIM API service.
 *
 * Wraps every network call for the eSIM module. Relies on the same shared
 * helpers used elsewhere in the codebase:
 *   - `doPost`         from /src/api/apiUtils
 *   - `getUserAgentId` from /src/shared/helper-utils/localStorage
 *
 * All eSIM dispatch calls share a single opid (MANAGEMATRIXESIM) and switch
 * behaviour through the `actioncode` parameter.
 */

import { doPost } from '../../../api/apiUtils';
import {
  APP_MAIN_URL,
  APP_USER_AUTH,
} from '../../../saas/AppStorageStaticNames';
import {
  getUserAgentId,
  getStringFromLS,
} from '../../../shared/helper-utils/localStorage';
import { ESIM_API } from '../constants/esimConstants';

/**
 * Defensive unwrap helper — every dispatch call returns the same envelope:
 *   { ApiStatus: { Status, StatusCode, Message, Result } }
 */
const unwrap = (response) => {
  const status = response?.ApiStatus || {};
  return {
    ok: String(status.StatusCode) === '200' || status.Status === 'OK',
    code: status.StatusCode,
    status: status.Status,
    message: status.Message || '',
    result: status.Result,
    raw: response,
  };
};

/**
 * Fetch the list of countries the eSIM service covers. The API returns an
 * envelope shaped like:
 *   { Result: { countries: ["USA", "India", ...], totalCount: 157 } }
 *
 * We normalise it to the same { value, label } shape used by AppSelect.
 */
export const getEsimCountriesApi = async () => {
  try {
    const request = {
      opid: ESIM_API.OPID,
      actioncode: ESIM_API.ACTIONS.GET_COUNTRIES,
      agentid: getUserAgentId(),
      resultformat: 'JSON',
    };
    const response = await doPost(request);
    const { ok, result } = unwrap(response);
    if (!ok || !result) return [];
    const list = Array.isArray(result?.countries) ? result.countries : [];
    return list
      .map((name) => ({
        value: name,
        label: name,
        countryName: name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getEsimCountriesApi failed:', error);
    return [];
  }
};

/**
 * Fetch the eSIM plans available for a given destination country.
 *
 * @param {Object} params
 * @param {string} params.country     country name as returned by GETCOUNTRIES
 * @param {number} [params.limit]
 * @param {number} [params.pageNo]
 * @returns {Promise<{ok:boolean, plans:Array, message:string}>}
 */
export const getEsimPlansApi = async ({
  country,
  limit = ESIM_API.PLANS_PAGE_SIZE,
  pageNo = 1,
} = {}) => {
  try {
    if (!country) {
      return { ok: false, plans: [], message: 'Country is required.' };
    }
    const request = {
      opid: ESIM_API.OPID,
      actioncode: ESIM_API.ACTIONS.GET_PLANS,
      agentid: getUserAgentId(),
      country_covered: country,
      limit,
      page_no: pageNo,
      resultformat: 'JSON',
    };
    const response = await doPost(request);
    const { ok, result, message } = unwrap(response);
    const plans = Array.isArray(result?.plans) ? result.plans : [];
    return { ok, plans, message };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getEsimPlansApi failed:', error);
    return { ok: false, plans: [], message: 'Failed to fetch eSIM plans.' };
  }
};

/**
 * Upload a passport / e-ticket / visa copy image to the shared S3 endpoint.
 * Mirrors the contract in onlineVisaApi.uploadVisaDocumentImageApi.
 *
 * @param {File} file
 * @returns {Promise<string|null>} the public URL of the uploaded asset.
 */
export const uploadEsimDocumentImageApi = async (file) => {
  if (!file) return null;
  try {
    const agentId = getUserAgentId();
    const sessiontoken = getStringFromLS(APP_USER_AUTH);
    const API_PATH = getStringFromLS(APP_MAIN_URL);
    const url = `${API_PATH}s3fileupload.jsp?agentid=${agentId}&sessiontoken=${sessiontoken}?resultformat=JSON`;
    const form = new FormData();
    form.append('file', file);
    form.append('uploadtype', 'esimdocument');
    // eslint-disable-next-line no-undef
    const res = await fetch(url, { method: 'post', body: form });
    const uploaded =
      res?.result ||
      res?.ApiStatus?.Result?.fileUrl ||
      res?.ApiStatus?.Result ||
      res?.Result?.url ||
      res?.url ||
      null;
    return typeof uploaded === 'string' ? uploaded : null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('uploadEsimDocumentImageApi failed:', error);
    return null;
  }
};

/**
 * Submit the eSIM booking. The API expects a single string field named
 * `bookingreq` whose value is JSON-encoded.
 *
 * @param {Object} bookingRequest
 * @returns {Promise<{ok:boolean, transactionId:string, message:string, response:any}>}
 */
export const performEsimBookingApi = async (bookingRequest) => {
  try {
    const request = {
      opid: ESIM_API.OPID,
      actioncode: ESIM_API.ACTIONS.PERFORM_ESIM_BOOKING,
      agentid: getUserAgentId(),
      bookingreq: JSON.stringify(bookingRequest || {}),
      resultformat: 'JSON',
    };
    const response = await doPost(request);
    const { ok, result, message } = unwrap(response);
    const apiStatus = response?.ApiStatus || {};
    const transactionId =
      typeof result === 'string'
        ? result
        : result?.txid || result?.Txid || apiStatus.TransactionStatus || '';
    return { ok: ok && !!transactionId, transactionId, message, response };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('performEsimBookingApi failed:', error);
    return {
      ok: false,
      transactionId: '',
      message: 'Booking request failed. Please retry.',
      response: null,
    };
  }
};

/**
 * Fetch a single eSIM transaction (used by the voucher page).
 *
 * @param {string} txid
 * @returns {Promise<Object|null>}
 */
export const getEsimDetailsApi = async (txid) => {
  if (!txid) return null;
  try {
    const request = {
      opid: ESIM_API.OPID,
      actioncode: ESIM_API.ACTIONS.GET_ESIM_DETAILS,
      agentid: getUserAgentId(),
      txid,
      resultformat: 'JSON',
    };
    const response = await doPost(request);
    const { ok, result } = unwrap(response);
    if (!ok) return null;
    if (Array.isArray(result)) return result[0] || null;
    return result || null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getEsimDetailsApi failed:', error);
    return null;
  }
};

/**
 * Fetch the agent's eSIM bookings within a date range.
 */
export const getEsimTransactionsApi = async (fromdate, todate) => {
  try {
    const request = {
      opid: ESIM_API.OPID,
      actioncode: ESIM_API.ACTIONS.GET_TRANSACTIONS,
      agentid: getUserAgentId(),
      fromdate,
      todate,
      resultformat: 'JSON',
    };
    const response = await doPost(request);
    const { ok, result } = unwrap(response);
    if (!ok) return [];
    return Array.isArray(result) ? result : [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getEsimTransactionsApi failed:', error);
    return [];
  }
};
